import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, unref as _unref, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, Fragment as _Fragment, renderList as _renderList } from "vue"

const _hoisted_1 = {
  class: "flex flex-col gap-4 text-left",
  "data-testid": "pin-step-2"
}
const _hoisted_2 = { class: "flex flex-wrap items-center gap-2" }
const _hoisted_3 = { class: "text-sm text-gray-600" }
const _hoisted_4 = /*#__PURE__*/_createElementVNode("p", { class: "text-sm text-gray-500" }, "Arraste o marcador até a entrada usada pelos médicos.", -1)
const _hoisted_5 = { class: "relative h-72 w-full overflow-hidden rounded-xl border border-gray-100" }
const _hoisted_6 = {
  key: 1,
  class: "flex h-full items-center justify-center text-sm text-gray-500"
}
const _hoisted_7 = { class: "flex flex-wrap items-center justify-between gap-2" }
const _hoisted_8 = { class: "inline-flex overflow-hidden rounded-lg border border-gray-200" }
const _hoisted_9 = { class: "flex items-center gap-3 text-xs text-gray-500" }
const _hoisted_10 = { class: "flex items-center gap-1" }
const _hoisted_11 = { class: "flex items-center gap-1" }
const _hoisted_12 = /*#__PURE__*/_createElementVNode("span", { class: "flex items-center gap-1" }, [
  /*#__PURE__*/_createElementVNode("span", { class: "h-2.5 w-2.5 rounded-full border border-primary" }),
  /*#__PURE__*/_createTextVNode(" raio ")
], -1)
const _hoisted_13 = {
  class: "font-mono text-xs text-gray-600",
  "data-testid": "pin-coordinates"
}
const _hoisted_14 = {
  key: 0,
  class: "text-xs text-gray-500",
  "data-testid": "pin-points-loading"
}
const _hoisted_15 = {
  key: 1,
  class: "rounded-xl border border-gray-100 bg-white p-4",
  "data-testid": "pin-doctors-card"
}
const _hoisted_16 = { class: "text-sm font-semibold text-gray-800" }
const _hoisted_17 = {
  key: 0,
  class: "mt-1 text-sm text-gray-500"
}
const _hoisted_18 = { class: "mt-3 flex flex-wrap gap-2" }
const _hoisted_19 = { class: "rounded-xl border border-gray-100 bg-white p-4" }
const _hoisted_20 = { class: "flex items-center gap-1.5 text-sm font-semibold text-gray-800" }
const _hoisted_21 = { class: "mt-3 flex flex-wrap items-center gap-2" }
const _hoisted_22 = ["onClick", "data-testid"]
const _hoisted_23 = {
  key: 0,
  class: "mt-2 text-sm text-alert",
  "data-testid": "pin-radius-off-preset-warning"
}
const _hoisted_24 = {
  key: 1,
  class: "mt-2 text-sm text-negative",
  "data-testid": "pin-radius-over-max-warning"
}

import { computed, ref } from 'vue'
import { ValidationError } from 'yup'
import { mdiCrosshairsGps, mdiMap, mdiMapMarkerRadiusOutline, mdiSatelliteVariant } from '@mdi/js'
import GoogleMaps from '@/components/GoogleMaps.vue'
import SVGIcon from '@/components/SVGIcon.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import GenericInput from '@/components/designSystem/Inputs/GenericInput.vue'
import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'
import type { LabelBadgeColor } from '@/components/designSystem/Badges/LabelBadge.vue'
import { ACCEPTED_COLOR, REJECTED_COLOR } from '@/components/CheckAttempts/checkAttemptsFormat'
import { formatMeters, haversineMeters, type LatLng } from '@/utils/geo'
import {
  HealthUnitSchema,
  REACH_RADIUS_HINT,
  REACH_RADIUS_MAX,
  REACH_RADIUS_MIN
} from '../../schemas'
import { RADIUS_CHIPS, unitTypeLabel } from '../../unitType'
import type { UnitGeolocationApi } from '../../composables/useUnitGeolocation'
import type { DoctorPoint } from './pinModalTypes'

type DoctorSummary = { total: number; rejected: number; doctors: number; days: number } | null


export default /*#__PURE__*/_defineComponent({
  __name: 'StepPosition',
  props: {
    geo: {},
    mapType: {},
    doctorPoints: {},
    doctorSummary: {},
    isLoadingPoints: { type: Boolean }
  },
  emits: ["update:mapType", "center-doctors"],
  setup(__props: any, { emit }) {

const props = __props;





const radiusFieldRef = ref<HTMLElement | null>(null)

const point = computed(() => props.geo.currentPoint.value)

const coordinates = computed(() =>
  point.value ? `${point.value.lat.toFixed(6)}, ${point.value.lng.toFixed(6)}` : '—'
)

const confidenceColor = computed<LabelBadgeColor>(() => {
  if (props.geo.origin.value === 'SUGGESTION') return 'blue'
  return props.geo.confidence.value === 'BUILDING' ? 'green' : 'yellow'
})

const confidenceLabel = computed(() => {
  if (props.geo.origin.value === 'SUGGESTION') return 'Sugestão dos checks'
  if (props.geo.confidence.value === 'BUILDING') return 'Prédio identificado (Google Places)'
  return 'Endereço aproximado'
})

const pointName = computed(() => props.geo.placeName.value || props.geo.formattedAddress.value)

/** Centro da nuvem de checks — o alvo do "Centralizar nos médicos" quando não há sugestão. */
const doctorsCentroid = computed<LatLng | null>(() => {
  if (!props.doctorPoints.length) return null
  const sum = props.doctorPoints.reduce(
    (acc, doctorPoint) => ({ lat: acc.lat + doctorPoint.lat, lng: acc.lng + doctorPoint.lng }),
    { lat: 0, lng: 0 }
  )
  return { lat: sum.lat / props.doctorPoints.length, lng: sum.lng / props.doctorPoints.length }
})

const doctorsTarget = computed(() => props.geo.suggestedPoint.value ?? doctorsCentroid.value)

const distanceToDoctorsM = computed(() =>
  point.value && doctorsTarget.value ? haversineMeters(point.value, doctorsTarget.value) : null
)

const showDoctorsCard = computed(
  () => !!props.geo.suggestedPoint.value || !!props.doctorSummary || !!props.doctorPoints.length
)

const originalRadiusOverMax = computed(
  () => (props.geo.originalRadius.value ?? 0) > REACH_RADIUS_MAX
)

/** Raio grande em metros cheios ("3.000 m"): em km o número perde a comparação com o preset. */
function formatRadius(meters: number | undefined): string {
  return meters == null ? '—' : `${meters.toLocaleString('pt-BR')} m`
}

function focusRadiusInput(): void {
  radiusFieldRef.value?.querySelector('input')?.focus()
}

/** Campo vazio não acusa erro: o gate é o "Continuar" do rodapé. */
const isBlank = (value: unknown) => value === '' || value === null || value === undefined

/**
 * Delega ao schema do formulário: limites e mensagens ficam num só lugar, iguais
 * às que o submit e o backend usariam para o mesmo valor.
 */
const reachRadiusRules = [
  (v: unknown) => {
    if (isBlank(v)) return true

    try {
      HealthUnitSchema.validateSyncAt('reachRadius', { reachRadius: v })
      return true
    } catch (error) {
      return error instanceof ValidationError ? error.message : REACH_RADIUS_HINT
    }
  }
]

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(LabelBadge, {
        size: "small",
        icon: null,
        color: confidenceColor.value,
        label: confidenceLabel.value,
        "data-testid": "pin-confidence-badge"
      }, null, 8, ["color", "label"]),
      _createElementVNode("span", _hoisted_3, _toDisplayString(pointName.value), 1)
    ]),
    _hoisted_4,
    _createElementVNode("div", _hoisted_5, [
      (point.value)
        ? (_openBlock(), _createBlock(GoogleMaps, {
            key: 0,
            latitude: point.value.lat,
            longitude: point.value.lng,
            radius: _ctx.geo.reachRadius.value != null ? Number(_ctx.geo.reachRadius.value) : undefined,
            address: _ctx.geo.formattedAddress.value,
            points: _ctx.doctorPoints,
            "fit-to-points": false,
            "map-type": _ctx.mapType,
            "show-info": false,
            onMarkerDragged: _ctx.geo.updateCoordinates
          }, null, 8, ["latitude", "longitude", "radius", "address", "points", "map-type", "onMarkerDragged"]))
        : (_openBlock(), _createElementBlock("div", _hoisted_6, " Sem coordenadas para exibir. Volte ao passo 1 e escolha um ponto. "))
    ]),
    _createElementVNode("div", _hoisted_7, [
      _createElementVNode("div", _hoisted_8, [
        _createElementVNode("button", {
          type: "button",
          class: _normalizeClass(["px-3 py-1.5 text-sm", _ctx.mapType === 'roadmap' ? 'bg-[#2F7F91] text-white' : 'bg-white text-gray-600']),
          onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('update:mapType', 'roadmap'))),
          "data-testid": "pin-map-type-roadmap"
        }, [
          _createVNode(SVGIcon, {
            path: _unref(mdiMap),
            class: "mr-1 inline-block"
          }, null, 8, ["path"]),
          _createTextVNode("Mapa ")
        ], 2),
        _createElementVNode("button", {
          type: "button",
          class: _normalizeClass(["px-3 py-1.5 text-sm", _ctx.mapType === 'satellite' ? 'bg-[#2F7F91] text-white' : 'bg-white text-gray-600']),
          onClick: _cache[1] || (_cache[1] = ($event: any) => (emit('update:mapType', 'satellite'))),
          "data-testid": "pin-map-type-satellite"
        }, [
          _createVNode(SVGIcon, {
            path: _unref(mdiSatelliteVariant),
            class: "mr-1 inline-block"
          }, null, 8, ["path"]),
          _createTextVNode("Satélite ")
        ], 2)
      ]),
      _createElementVNode("p", _hoisted_9, [
        _createElementVNode("span", _hoisted_10, [
          _createElementVNode("span", {
            class: "h-2.5 w-2.5 rounded-full",
            style: _normalizeStyle({ backgroundColor: _unref(ACCEPTED_COLOR) })
          }, null, 4),
          _createTextVNode(" check aceito ")
        ]),
        _createElementVNode("span", _hoisted_11, [
          _createElementVNode("span", {
            class: "h-2.5 w-2.5 rounded-full",
            style: _normalizeStyle({ backgroundColor: _unref(REJECTED_COLOR) })
          }, null, 4),
          _createTextVNode(" rejeitado ")
        ]),
        _hoisted_12
      ]),
      _createElementVNode("p", _hoisted_13, _toDisplayString(coordinates.value), 1)
    ]),
    (_ctx.isLoadingPoints)
      ? (_openBlock(), _createElementBlock("p", _hoisted_14, " Carregando os checks dos médicos dos últimos 14 dias... "))
      : _createCommentVNode("", true),
    (showDoctorsCard.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
          _createElementVNode("p", _hoisted_16, [
            (distanceToDoctorsM.value != null)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createTextVNode(" Os médicos realizam o check a " + _toDisplayString(_unref(formatMeters)(distanceToDoctorsM.value)) + " do pino atual ", 1)
                ], 64))
              : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                  _createTextVNode("Checks dos médicos nos últimos 14 dias")
                ], 64))
          ]),
          (_ctx.doctorSummary)
            ? (_openBlock(), _createElementBlock("p", _hoisted_17, _toDisplayString(_ctx.doctorSummary.total) + " checks de " + _toDisplayString(_ctx.doctorSummary.doctors) + " " + _toDisplayString(_ctx.doctorSummary.doctors === 1 ? 'médico' : 'médicos') + " em " + _toDisplayString(_ctx.doctorSummary.days) + " " + _toDisplayString(_ctx.doctorSummary.days === 1 ? 'dia' : 'dias') + ". ", 1))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_18, [
            _createVNode(FilledButton, {
              text: "Centralizar nos médicos",
              width: "auto",
              height: "40px",
              class: "!w-auto !px-4 text-sm",
              "prepend-icon": _unref(mdiCrosshairsGps),
              onClick: _cache[2] || (_cache[2] = ($event: any) => (emit('center-doctors'))),
              "data-testid": "pin-center-doctors-button"
            }, null, 8, ["prepend-icon"]),
            (_ctx.geo.canResetLocation.value)
              ? (_openBlock(), _createBlock(OutlinedButton, {
                  key: 0,
                  text: "Voltar ao pino atual",
                  width: "auto",
                  height: "40px",
                  class: "!w-auto !px-4 text-sm",
                  onClick: _cache[3] || (_cache[3] = ($event: any) => (_ctx.geo.resetLocation())),
                  "data-testid": "pin-reset-location-button"
                }))
              : _createCommentVNode("", true)
          ])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_19, [
      _createElementVNode("p", _hoisted_20, [
        _createVNode(SVGIcon, {
          path: _unref(mdiMapMarkerRadiusOutline),
          class: "text-base text-primary"
        }, null, 8, ["path"]),
        _createTextVNode(" Raio de alcance · preset " + _toDisplayString(_unref(unitTypeLabel)(_ctx.geo.unitType.value)), 1)
      ]),
      _createElementVNode("div", _hoisted_21, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(RADIUS_CHIPS), (chip) => {
          return (_openBlock(), _createElementBlock("button", {
            key: chip,
            type: "button",
            class: _normalizeClass(["rounded-full border px-3 py-1 text-sm", 
            _ctx.geo.reachRadius.value === chip
              ? 'border-[#2F7F91] bg-[#2F7F91] text-white'
              : 'border-gray-200 bg-white text-gray-600'
          ]),
            onClick: ($event: any) => (_ctx.geo.setReachRadius(chip)),
            "data-testid": `pin-radius-chip-${chip}`
          }, _toDisplayString(chip) + " m ", 11, _hoisted_22))
        }), 128)),
        _createElementVNode("button", {
          type: "button",
          class: "rounded-full border border-gray-200 bg-white px-3 py-1 text-sm text-gray-600",
          onClick: _cache[4] || (_cache[4] = ($event: any) => (focusRadiusInput())),
          "data-testid": "pin-radius-chip-other"
        }, " Outro ")
      ]),
      _createElementVNode("div", {
        ref_key: "radiusFieldRef",
        ref: radiusFieldRef,
        class: "mt-3 w-full sm:max-w-[220px]"
      }, [
        _createVNode(GenericInput, {
          type: "number",
          label: "Raio (m)",
          "model-value": _ctx.geo.reachRadius.value,
          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => (_ctx.geo.setReachRadius($event))),
          rules: reachRadiusRules,
          min: _unref(REACH_RADIUS_MIN),
          max: _unref(REACH_RADIUS_MAX),
          hint: _unref(REACH_RADIUS_HINT),
          "persistent-hint": "",
          "data-testid": "reach-radius-input"
        }, null, 8, ["model-value", "min", "max", "hint"])
      ], 512),
      (_ctx.geo.isRadiusOffPreset.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_23, " Fora do preset do tipo (" + _toDisplayString(_ctx.geo.radiusPreset.value) + " m). Justifique no próximo passo. ", 1))
        : _createCommentVNode("", true),
      (originalRadiusOverMax.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_24, " Raio atual é " + _toDisplayString(formatRadius(_ctx.geo.originalRadius.value)) + " e escondia o erro. Acima de " + _toDisplayString(_unref(REACH_RADIUS_MAX)) + " m não é permitido. ", 1))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})